import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatIQD, formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import StatusBadge from "../components/StatusBadge";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeAccount() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [confirm, setConfirm] = useState(null); // { type, id, description }

  const load = () => {
    api.get(`/employees/${id}/summary`).then((r) => setData(r.data));
    api.get(`/employees/${id}/transactions`, { params: { from_date: from || undefined, to_date: to || undefined } }).then((r) => setTxns(r.data));
  };

  useEffect(() => { load(); }, [id, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  const doDelete = async () => {
    if (!confirm) return;
    const url = confirm.type === "order" ? `/employee-food-orders/${confirm.id}` : `/payments/${confirm.id}`;
    try {
      const { data: res } = await api.delete(url);
      toast.success(confirm.type === "order"
        ? `Order deleted${res.payments_reallocated ? ` · ${res.payments_reallocated} payment(s) re-allocated` : ""}`
        : "Payment deleted and allocations reversed");
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (!data) return <div className="p-6 text-muted-foreground">Loading…</div>;
  const e = data.employee;

  return (
    <div className="space-y-6" data-testid="employee-account-page">
      <div className="flex items-center gap-3">
        <Link to="/balances"><Button variant="ghost" size="sm"><ArrowLeft size={16} className="mr-1" />Back</Button></Link>
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h1 className="heading text-2xl font-extrabold">{e.name}</h1>
            <div className="text-sm text-muted-foreground">{e.phone || "—"} · {e.email || "—"}</div>
            <div className="mt-2"><StatusBadge status={e.status} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-right">
            <div><div className="text-[11px] uppercase text-muted-foreground">Orders</div><div className="heading text-xl font-bold">{data.total_orders}</div></div>
            <div><div className="text-[11px] uppercase text-muted-foreground">Food Cost</div><div className="heading text-xl font-bold">{formatIQD(data.total_cost)}</div></div>
            <div><div className="text-[11px] uppercase text-muted-foreground">Paid</div><div className="heading text-xl font-bold text-emerald-700">{formatIQD(data.total_paid)}</div></div>
            <div><div className="text-[11px] uppercase text-muted-foreground">Outstanding</div><div className={`heading text-xl font-bold ${data.outstanding > 0 ? "text-rose-700" : "text-slate-600"}`} data-testid="acc-outstanding">{formatIQD(data.outstanding)}</div></div>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex gap-3 items-end flex-wrap">
          <div><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1.5" /></div>
          <div><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1.5" /></div>
          <Button variant="outline" onClick={() => { setFrom(""); setTo(""); }}>Clear</Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b heading font-bold">Transaction History</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Description</th>
                <th className="p-3 text-right">Amount</th><th className="p-3 text-right">Balance</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={`${t.type}-${t.id}`} className="border-t table-row-hover" data-testid={`txn-${t.id}`}>
                  <td className="p-3">{formatDate(t.date)}</td>
                  <td className="p-3">{t.type === "order" ? <span className="text-rose-700 font-medium">Food Order</span> : <span className="text-emerald-700 font-medium">Payment</span>}</td>
                  <td className="p-3">{t.description}{t.restaurant ? ` · ${t.restaurant}` : ""}</td>
                  <td className={`p-3 text-right mono ${t.type === "order" ? "text-rose-700" : "text-emerald-700"}`}>{t.type === "order" ? "+" : "-"}{formatIQD(t.amount)}</td>
                  <td className="p-3 text-right mono font-semibold">{formatIQD(t.balance_after)}</td>
                  <td className="p-3">{t.status ? <StatusBadge status={t.status} /> : "—"}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                      onClick={() => setConfirm({ type: t.type, id: t.id, description: `${t.type === "order" ? "Food Order" : "Payment"} · ${formatIQD(t.amount)} · ${formatDate(t.date)}` })}
                      data-testid={`btn-delete-txn-${t.id}`}
                    >
                      <Trash2 size={14} className="mr-1" />Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {txns.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No transactions in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {confirm?.type === "order" ? "food order" : "payment"}?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium text-slate-900 dark:text-slate-100 mb-2">{confirm?.description}</span>
              This permanently removes the transaction. {confirm?.type === "order"
                ? "If it had payments applied, those payments will be automatically re-allocated to the employee's remaining unpaid orders (FIFO)."
                : "All allocations to orders will be reversed and their balances restored."} An audit-log entry is recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="btn-confirm-delete" className="bg-rose-600 hover:bg-rose-700">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
